import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState, useCallback } from 'react';
import {
	Box,
	DialogContent,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Switch,
	FormControlLabel,
	SelectChangeEvent,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { ETableStructureType } from 'ui/EasTable/types';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { TAdditionalService } from 'redux/reducers/emManagement/types';
import { TEditParamsDialogProps } from './types';
import { validationSchema } from './validationSchema';
import { TBoxStructureItem } from '../../../EMManagement/EMManagementId/EasParamsBox/types';

export const EditParamsDialog = <T,>({
	open,
	setClose,
	data,
	boxStructure,
	onSave,
	title,
	additionalServices,
}: TEditParamsDialogProps<T>) => {
	const countryList = useSelector(getCountryListData);
	const [formValues, setFormValues] = useState<Record<string, any>>({});

	const [selectedService, setSelectedService] = useState<TAdditionalService | null>(null);

	const reset = () => {
		setFormValues({});
		setSelectedService(null);
	};

	const formik = useFormik({
		initialValues: formValues,
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			onSave(values);
			formik.resetForm();
			reset();
			setClose();
		},
	});

	const clearServicePeriod = useCallback(() => {
		if (formik.values.isRecurring === false && formik.values.servicePeriod) {
			formik.setFieldValue('servicePeriod', null).catch(() => {});
		}
	}, [formik]);

	useEffect(() => {
		clearServicePeriod();
	}, [clearServicePeriod]);

	const [prevData, setPrevData] = useState(data);
	const [prevOpen, setPrevOpen] = useState(open);

	if (data !== prevData || open !== prevOpen) {
		setPrevData(data);
		setPrevOpen(open);

		if (open) {
			const initialValues: Record<string, any> = {};
			boxStructure.body.forEach((item: TBoxStructureItem<any>) => {
				if (item.editable) {
					const fieldName = String(item.selector);
					const emptyValue = item.type === ETableStructureType.BOOLEAN ? false : '';
					initialValues[fieldName] = data
						? (data[fieldName as keyof typeof data] ?? emptyValue)
						: emptyValue;
				}
			});
			setFormValues(initialValues);
		}
	}

	const renderField = (item: TBoxStructureItem<any>) => {
		const fieldName = String(item.selector);
		const fieldValue = formik.values[fieldName];

		// Hide Service Period field when isRecurring is false
		if (fieldName === 'servicePeriod') {
			const { isRecurring } = formik.values;
			if (!isRecurring) {
				return null;
			}
		}

		switch (item.type) {
			case ETableStructureType.BOOLEAN:
				return (
					<FormControlLabel
						control={
							<Switch
								checked={fieldValue === true || fieldValue === 'true'}
								onChange={(e) => {
									formik.setFieldValue(fieldName, e.target.checked).catch(() => {});
								}}
								name={fieldName}
							/>
						}
						label={item.name}
					/>
				);

			case ETableStructureType.DATE:
				return (
					<DatePickerComponent
						id={fieldName}
						label={item.name}
						value={fieldValue ? DateTime.fromISO(fieldValue) : null}
						onChange={(date) => {
							const isoUtc = date ? date.toUTC().toISO({ suppressMilliseconds: false }) : '';
							formik.setFieldValue(fieldName, isoUtc || '').catch(() => {});
						}}
					/>
				);

			case ETableStructureType.COUNTRY_CODE:
			case ETableStructureType.COUNTRY_NUMBER:
				return (
					<CountrySelectMenu
						countryList={
							countryList?.map((countryItem) => ({
								code: countryItem.isoAlpha2,
								label: countryItem.name,
								number: countryItem.id,
							})) ?? []
						}
						onChange={(countryCode) => () => {}}
						onChangeNumber={(countryNumber) => {
							formik.setFieldValue(fieldName, countryNumber).catch(() => {});
						}}
						countryCode={fieldValue}
						countryNumber={fieldValue}
						size="small"
						type={item.type}
					/>
				);

			case ETableStructureType.TEXT:
			case ETableStructureType.MULTILINE_TEXT:
				return (
					<TextField
						id={fieldName}
						name={fieldName}
						size="small"
						label={item.name}
						value={fieldValue}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						multiline
						rows={3}
						fullWidth
						error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
						helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
					/>
				);

			case ETableStructureType.NUMBER:
				return (
					<TextField
						id={fieldName}
						name={fieldName}
						size="small"
						label={item.name}
						value={fieldValue}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						type="number"
						fullWidth
						error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
						helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
					/>
				);

			case ETableStructureType.STATUS:
				return (
					<FormControl fullWidth size="small">
						<InputLabel id={`${fieldName}-label`}>{item.name}</InputLabel>
						<Select
							id={fieldName}
							labelId={`${fieldName}-label`}
							label={item.name}
							name={fieldName}
							value={fieldValue}
							onChange={formik.handleChange}
						>
							<MenuItem value="Active">Active</MenuItem>
							<MenuItem value="Deactivated">Deactivated</MenuItem>
							<MenuItem value="Blocked">Blocked</MenuItem>
						</Select>
					</FormControl>
				);

			default:
				return (
					<TextField
						id={fieldName}
						name={fieldName}
						size="small"
						label={item.name}
						value={fieldValue}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						fullWidth
						error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
						helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
					/>
				);
		}
	};

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle
				color="primary"
				dangerouslySetInnerHTML={{ __html: title || `Edit ${String(boxStructure.title)}` }}
			/>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent
					sx={{
						p: '12px 12px 12px 12px',
					}}
				>
					<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
						{boxStructure.body
							?.filter((item: TBoxStructureItem<any>) => item.editable)
							?.map((item: TBoxStructureItem<any>) => (
								<Box key={String(item.selector)}>{renderField(item)}</Box>
							))}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							formik.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					<Button type="submit" autoFocus>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
